import { useState, useEffect, useCallback } from "react";
import { sendSignInLinkToEmail } from "firebase/auth";
import { auth } from "@/firebase";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ACTION_CODE_SETTINGS = {
  url: `${window.location.origin}/admin/setup`,
  handleCodeInApp: true,
};

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("hafani_admin_token")}`,
});

export const SuperAdminPage = () => {
  const navigate = useNavigate();

  const [associations, setAssociations] = useState([]);
  const [loadingAssoc, setLoadingAssoc] = useState(true);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);

  const [admins, setAdmins] = useState([]);
  const [loadingAdmins, setLoadingAdmins] = useState(true);
  // editingAdmin: { admin_id, current association_id } | null
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [editAssocId, setEditAssocId] = useState("");
  const [savingAssoc, setSavingAssoc] = useState(false);

  const [activePanel, setActivePanel] = useState(null);
  const [assocForm, setAssocForm] = useState({ name: "", slug: "", contact_email: "", city: "" });
  const [assocLoading, setAssocLoading] = useState(false);
  const [newAssociation, setNewAssociation] = useState(null);
  const [adminForm, setAdminForm] = useState({ email: "", association_id: "" });
  const [adminLoading, setAdminLoading] = useState(false);

  const handleAuthError = useCallback((err) => {
    if (err.response?.status === 401 || err.response?.status === 403) {
      localStorage.removeItem("hafani_admin_token");
      localStorage.removeItem("hafani_admin_role");
      navigate("/admin/login");
      return true;
    }
    return false;
  }, [navigate]);

  const fetchAssociations = useCallback(async () => {
    setLoadingAssoc(true);
    try {
      const res = await axios.get(`${API}/associations/`, { headers: authHeaders() });
      setAssociations(res.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Impossible de charger les associations.");
    } finally {
      setLoadingAssoc(false);
    }
  }, [handleAuthError]);

  const fetchPending = useCallback(async () => {
    setLoadingPending(true);
    try {
      const res = await axios.get(`${API}/auth/admin/pending`, { headers: authHeaders() });
      setPendingRequests(res.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Impossible de charger les demandes.");
    } finally {
      setLoadingPending(false);
    }
  }, [handleAuthError]);

  const fetchAdmins = useCallback(async () => {
    setLoadingAdmins(true);
    try {
      const res = await axios.get(`${API}/auth/admin/list`, { headers: authHeaders() });
      setAdmins(res.data);
    } catch (err) {
      if (!handleAuthError(err)) toast.error("Impossible de charger les administrateurs.");
    } finally {
      setLoadingAdmins(false);
    }
  }, [handleAuthError]);

  useEffect(() => {
    fetchAssociations();
    fetchPending();
    fetchAdmins();
  }, [fetchAssociations, fetchPending, fetchAdmins]);

  const handleLogout = () => {
    localStorage.removeItem("hafani_admin_token");
    localStorage.removeItem("hafani_admin_role");
    navigate("/admin/login");
  };

  const handleApprove = async (req) => {
    setApprovingId(req.admin_id);
    try {
      const res = await axios.patch(
        `${API}/auth/admin/${req.admin_id}/approve`,
        {},
        { headers: authHeaders() }
      );
      await sendSignInLinkToEmail(auth, res.data.email, ACTION_CODE_SETTINGS);
      window.localStorage.setItem("hafani_invite_email", res.data.email);

      toast.success(`Compte approuvé. Invitation envoyée à ${res.data.email}.`);
      setPendingRequests((prev) => prev.filter((r) => r.admin_id !== req.admin_id));
      fetchAssociations();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'approbation.");
    } finally {
      setApprovingId(null);
    }
  };

  const handleReject = async (req) => {
    setRejectingId(req.admin_id);
    try {
      await axios.delete(`${API}/auth/admin/${req.admin_id}/reject`, { headers: authHeaders() });
      toast.success("Demande rejetée.");
      setPendingRequests((prev) => prev.filter((r) => r.admin_id !== req.admin_id));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du rejet.");
    } finally {
      setRejectingId(null);
    }
  };

  const handleSaveAssociation = async () => {
    setSavingAssoc(true);
    try {
      await axios.patch(
        `${API}/auth/admin/${editingAdmin}/association`,
        { association_id: editAssocId || null },
        { headers: authHeaders() }
      );
      toast.success("Association mise à jour.");
      setEditingAdmin(null);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la mise à jour.");
    } finally {
      setSavingAssoc(false);
    }
  };

  const handleCreateAssociation = async (e) => {
    e.preventDefault();
    setAssocLoading(true);
    try {
      const res = await axios.post(`${API}/associations/`, assocForm, { headers: authHeaders() });
      setNewAssociation(res.data);
      setAssociations((prev) => [...prev, res.data]);
      setActivePanel("new-admin");
      setAdminForm((f) => ({ ...f, association_id: res.data.id }));
      toast.success(`Association "${res.data.name}" créée.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la création.");
    } finally {
      setAssocLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setAdminLoading(true);
    try {
      const res = await axios.post(
        `${API}/auth/admin/register`,
        { email: adminForm.email, association_id: adminForm.association_id },
        { headers: authHeaders() }
      );
      await sendSignInLinkToEmail(auth, res.data.email, ACTION_CODE_SETTINGS);
      window.localStorage.setItem("hafani_invite_email", res.data.email);

      toast.success(`Invitation envoyée à ${adminForm.email}.`);
      setAdminForm({ email: "", association_id: "" });
      setNewAssociation(null);
      setActivePanel(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'invitation.");
    } finally {
      setAdminLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: "#EAE8E4", fontFamily: "'DM Sans', sans-serif" }}
    >
      {/* Header */}
      <header className="bg-white border-b border-slate-200/60 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span
              className="text-lg text-slate-900"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
            >
              Hafani MS
            </span>
            <span className="text-slate-300">|</span>
            <span className="text-xs font-medium tracking-wide text-emerald-600 uppercase">Super Admin</span>
          </div>
          <button
            onClick={handleLogout}
            className="text-xs text-slate-500 hover:text-slate-700 transition-colors px-3 py-1.5 rounded-lg hover:bg-slate-100"
          >
            Déconnexion
          </button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 space-y-8">
        {/* Stat bar */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard label="Associations" value={associations.length} />
          <StatCard label="En attente" value={pendingRequests.length} highlight={pendingRequests.length > 0} />
          <StatCard label="Membres total" value="—" />
        </div>

        {/* Pending requests */}
        {!loadingPending && pendingRequests.length > 0 && (
          <section>
            <div className="flex items-center gap-2.5 mb-4">
              <h2
                className="text-2xl text-slate-900"
                style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
              >
                Demandes en attente
              </h2>
              <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">
                {pendingRequests.length}
              </span>
            </div>
            <div className="space-y-3">
              {pendingRequests.map((req) => (
                <div
                  key={req.admin_id}
                  className="bg-white rounded-2xl border border-amber-100 shadow-sm p-5 flex flex-col sm:flex-row sm:items-center gap-4"
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-slate-900 text-sm">{req.association_name}</p>
                      <span className="inline-block px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 text-xs font-mono">
                        {req.association_slug}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{req.email}</p>
                    <div className="flex items-center gap-3 mt-1">
                      {req.city && <span className="text-xs text-slate-400">{req.city}</span>}
                      <span className="text-xs text-slate-300">
                        {new Date(req.created_at).toLocaleDateString("fr-FR")}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleReject(req)}
                      disabled={rejectingId === req.admin_id || approvingId === req.admin_id}
                      className="px-4 py-2 rounded-xl border border-slate-200 text-slate-500 text-xs font-medium hover:bg-slate-50 hover:text-red-500 hover:border-red-200 transition-all disabled:opacity-40"
                    >
                      {rejectingId === req.admin_id ? "…" : "Rejeter"}
                    </button>
                    <button
                      onClick={() => handleApprove(req)}
                      disabled={approvingId === req.admin_id || rejectingId === req.admin_id}
                      className="px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:opacity-40"
                    >
                      {approvingId === req.admin_id ? "Envoi…" : "Approuver & inviter"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Associations section */}
        <section>
          <div className="flex items-center justify-between mb-4">
            <h2
              className="text-2xl text-slate-900"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
            >
              Associations
            </h2>
            <button
              onClick={() => setActivePanel(activePanel === "new-association" ? null : "new-association")}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
              Nouvelle association
            </button>
          </div>

          {activePanel === "new-association" && (
            <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6 mb-4">
              <h3 className="text-sm font-medium text-slate-900 mb-5">Créer une association</h3>
              <form onSubmit={handleCreateAssociation} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <FormField label="Nom de l'association" value={assocForm.name}
                  onChange={(v) => setAssocForm((f) => ({ ...f, name: v }))} required placeholder="ex. AMPA France" />
                <FormField label="Identifiant (slug)" value={assocForm.slug}
                  onChange={(v) => setAssocForm((f) => ({ ...f, slug: v.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "") }))}
                  required placeholder="ex. ampa-france" />
                <FormField label="Email de contact" type="email" value={assocForm.contact_email}
                  onChange={(v) => setAssocForm((f) => ({ ...f, contact_email: v }))} placeholder="contact@association.fr" />
                <FormField label="Ville" value={assocForm.city}
                  onChange={(v) => setAssocForm((f) => ({ ...f, city: v }))} placeholder="Paris" />
                <div className="sm:col-span-2 flex gap-3 justify-end pt-2">
                  <button type="button" onClick={() => setActivePanel(null)}
                    className="px-4 py-2 rounded-xl text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-all">
                    Annuler
                  </button>
                  <button type="submit" disabled={assocLoading}
                    className="px-6 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:opacity-50">
                    {assocLoading ? "Création…" : "Créer l'association"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activePanel === "new-admin" && newAssociation && (
            <div className="bg-white rounded-2xl border border-emerald-200 shadow-sm p-6 mb-4">
              <div className="flex items-start gap-3 mb-5">
                <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <svg className="w-4 h-4 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-900">Association créée</p>
                  <p className="text-xs text-slate-500 mt-0.5">
                    Invitez un administrateur pour <strong>{newAssociation.name}</strong>.
                  </p>
                </div>
              </div>
              <form onSubmit={handleCreateAdmin} className="space-y-4">
                <FormField label="Email de l'administrateur" type="email" value={adminForm.email}
                  onChange={(v) => setAdminForm((f) => ({ ...f, email: v }))} required placeholder="admin@association.fr" />
                <div className="flex gap-3 justify-end">
                  <button type="button" onClick={() => { setActivePanel(null); setNewAssociation(null); }}
                    className="px-4 py-2 rounded-xl text-sm text-slate-500 hover:text-slate-700 hover:bg-slate-100 transition-all">
                    Plus tard
                  </button>
                  <button type="submit" disabled={adminLoading}
                    className="px-6 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:opacity-50">
                    {adminLoading ? "Envoi…" : "Envoyer l'invitation"}
                  </button>
                </div>
              </form>
            </div>
          )}

          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
            {loadingAssoc ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : associations.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-32 text-slate-400 text-sm gap-2">
                <svg className="w-8 h-8 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                <p>Aucune association pour l'instant.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Nom</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden sm:table-cell">Slug</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden md:table-cell">Ville</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden lg:table-cell">Email</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Créée le</th>
                  </tr>
                </thead>
                <tbody>
                  {associations.map((a, i) => (
                    <tr key={a.id}
                      className={`border-b border-slate-50 hover:bg-slate-50/50 transition-colors ${i === associations.length - 1 ? "border-0" : ""}`}>
                      <td className="px-5 py-3.5 font-medium text-slate-900">{a.name}</td>
                      <td className="px-5 py-3.5 text-slate-500 font-mono text-xs hidden sm:table-cell">{a.slug}</td>
                      <td className="px-5 py-3.5 text-slate-500 hidden md:table-cell">{a.city || "—"}</td>
                      <td className="px-5 py-3.5 text-slate-500 hidden lg:table-cell">{a.contact_email || "—"}</td>
                      <td className="px-5 py-3.5 text-slate-400 text-xs">
                        {new Date(a.created_at).toLocaleDateString("fr-FR")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Admins list */}
        <section>
          <h2
            className="text-2xl text-slate-900 mb-4"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
          >
            Administrateurs
          </h2>
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
            {loadingAdmins ? (
              <div className="flex items-center justify-center h-24">
                <div className="w-5 h-5 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : admins.length === 0 ? (
              <div className="flex items-center justify-center h-24 text-slate-400 text-sm">
                Aucun administrateur pour l'instant.
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide">Email</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden sm:table-cell">Association</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide hidden md:table-cell">Statut</th>
                    <th className="px-5 py-3 text-xs font-medium text-slate-500 uppercase tracking-wide text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin, i) => (
                    <tr
                      key={admin.admin_id}
                      className={`border-b border-slate-50 hover:bg-slate-50/40 transition-colors ${i === admins.length - 1 ? "border-0" : ""}`}
                    >
                      <td className="px-5 py-3.5 text-slate-700">{admin.email}</td>
                      <td className="px-5 py-3.5 hidden sm:table-cell">
                        {editingAdmin === admin.admin_id ? (
                          <div className="flex items-center gap-2">
                            <select
                              value={editAssocId}
                              onChange={(e) => setEditAssocId(e.target.value)}
                              className="px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-slate-900 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
                            >
                              <option value="">Aucune</option>
                              {associations.map((a) => (
                                <option key={a.id} value={a.id}>{a.name}</option>
                              ))}
                            </select>
                            <button
                              onClick={handleSaveAssociation}
                              disabled={savingAssoc}
                              className="px-3 py-1.5 rounded-lg bg-emerald-600 text-white text-xs font-medium hover:bg-emerald-700 disabled:opacity-50 transition-all"
                            >
                              {savingAssoc ? "…" : "Enregistrer"}
                            </button>
                            <button
                              onClick={() => setEditingAdmin(null)}
                              className="px-3 py-1.5 rounded-lg text-slate-400 hover:text-slate-600 text-xs transition-colors"
                            >
                              Annuler
                            </button>
                          </div>
                        ) : (
                          <span className={admin.association_name ? "text-slate-700" : "text-slate-400 italic"}>
                            {admin.association_name || "Non assignée"}
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3.5 hidden md:table-cell">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          admin.status === "active"
                            ? "bg-emerald-50 text-emerald-700"
                            : "bg-amber-50 text-amber-700"
                        }`}>
                          {admin.status === "active" ? "Actif" : "En attente"}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 text-right">
                        {editingAdmin !== admin.admin_id && (
                          <button
                            onClick={() => {
                              setEditingAdmin(admin.admin_id);
                              setEditAssocId(admin.association_id || "");
                            }}
                            className="text-xs text-emerald-600 hover:text-emerald-700 font-medium transition-colors"
                          >
                            Modifier
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Invite standalone admin */}
        <section>
          <h2
            className="text-2xl text-slate-900 mb-4"
            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
          >
            Inviter un administrateur
          </h2>
          <div className="bg-white rounded-2xl border border-slate-200/60 shadow-sm p-6">
            <p className="text-sm text-slate-500 mb-5">
              Ajoutez un administrateur à une association existante et envoyez-lui un lien d'activation.
            </p>
            <form onSubmit={handleCreateAdmin} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Email de l'administrateur" type="email" value={adminForm.email}
                onChange={(v) => setAdminForm((f) => ({ ...f, email: v }))} required placeholder="admin@association.fr" />
              <div className="space-y-1.5">
                <label className="block text-xs font-medium tracking-wide text-slate-500 uppercase">
                  Association <span className="text-red-400">*</span>
                </label>
                <select value={adminForm.association_id}
                  onChange={(e) => setAdminForm((f) => ({ ...f, association_id: e.target.value }))} required
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all">
                  <option value="">Sélectionner une association</option>
                  {associations.map((a) => (
                    <option key={a.id} value={a.id}>{a.name}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2 flex justify-end">
                <button type="submit" disabled={adminLoading}
                  className="px-6 py-2 rounded-xl bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 active:scale-[0.98] transition-all disabled:opacity-50">
                  {adminLoading ? "Envoi…" : "Envoyer l'invitation"}
                </button>
              </div>
            </form>
          </div>
        </section>
      </main>
    </div>
  );
};

const StatCard = ({ label, value, highlight = false }) => (
  <div className={`rounded-2xl border shadow-sm px-6 py-5 ${highlight ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200/60"}`}>
    <p className={`text-xs font-medium tracking-wide uppercase mb-1 ${highlight ? "text-amber-600" : "text-slate-500"}`}>{label}</p>
    <p
      className={`text-3xl ${highlight ? "text-amber-700" : "text-slate-900"}`}
      style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontWeight: 500 }}
    >
      {value}
    </p>
  </div>
);

const FormField = ({ label, type = "text", value, onChange, required, placeholder }) => (
  <div className="space-y-1.5">
    <label className="block text-xs font-medium tracking-wide text-slate-500 uppercase">
      {label}{required && <span className="text-red-400 ml-0.5">*</span>}
    </label>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-900 text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-500 transition-all"
    />
  </div>
);
